import { faUser } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FC, ReactNode } from "react";

import * as styles from "./Avatar.styles";

const AVATAR_SIZES = {
  xsmall: "xsmall",
  small: "small",
  normal: "normal",
  large: "large",
  xlarge: "xlarge",
};

interface AvatarProps {
  /**
   * Profile photo (if uploaded) for the user
   */
  image?: string;
  /**
   * User's first name
   */
  firstName?: string;
  /**
   * User's last name
   */
  lastName?: string;
  /**
   * Specify if the avatar is using the "light" profile, has a white background with dark text. To be used on dark backgrounds only
   *
   * @default false
   */
  isLight?: boolean;
  /**
   * size of the Avatar
   */
  size?: keyof typeof AVATAR_SIZES;
  /**
   * Label, required for aria purposes when an image is present
   */
  label: string;
  /**
   * Position of the children content overlayed on the Avatar
   */
  overlayPosition?: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" | "center";
  children?: ReactNode;
}

const Avatar: FC<AvatarProps> = ({
  image,
  firstName,
  lastName,
  label,
  isLight,
  size,
  overlayPosition,
  children,
  ...rest
}) => {
  const wrapperStyles = [styles.avatar, size && styles.sizes[size], isLight && styles.isLight];

  const overlayChipStyles = [
    styles.overlayChip,
    overlayPosition && styles.overlayPosition[overlayPosition](size),
  ];

  const canRenderName = firstName && lastName && firstName.length > 0 && lastName.length > 0;

  const getNonAvatarContents = () => {
    if (canRenderName) {
      const initials = firstName[0] + lastName[0];

      return initials.toUpperCase();
    }

    return <FontAwesomeIcon icon={faUser} />;
  };

  const renderOverlayChip = () => {
    return children && <div css={overlayChipStyles}>{children}</div>;
  };

  const renderImageOrName = () => {
    if (image) {
      return <img src={image} css={styles.image} alt={label} aria-hidden={!label} />;
    }

    return <span>{getNonAvatarContents()}</span>;
  };

  return (
    <div css={wrapperStyles} {...rest}>
      {renderImageOrName()}
      {renderOverlayChip()}
    </div>
  );
};

Avatar.defaultProps = {
  overlayPosition: "bottomLeft",
  size: "normal",
};

export { Avatar, AVATAR_SIZES, type AvatarProps };
